// Version checking utility to detect when app needs refresh
const VERSION_CHECK_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes (reasonable for production)
const VERSION_STORAGE_KEY = 'app_version';

// Get current version from meta tag in the HTML
function getCurrentVersion(): string {
  const metaTag = document.querySelector('meta[data-version]');
  const version = metaTag?.getAttribute('data-version');
  return version || import.meta.env.VITE_APP_VERSION || Date.now().toString();
}

interface VersionInfo {
  version: string;
  timestamp: number;
}

class VersionChecker {
  private currentVersion: string;
  private checkInterval: NodeJS.Timeout | null = null;
  private onUpdateAvailable?: () => void;

  constructor() {
    this.currentVersion = getCurrentVersion();
    console.log('🔍 VersionChecker initialized with version:', this.currentVersion);
    this.initializeVersion();
  }

  private initializeVersion() {
    // Store current version on first load
    const stored = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!stored) {
      this.storeVersion();
    } else {
      // Check if we have a version mismatch on startup
      const versionInfo: VersionInfo = JSON.parse(stored);
      if (versionInfo.version !== this.currentVersion) {
        console.log('🔄 New app version detected:', {
          old: versionInfo.version,
          new: this.currentVersion
        });
        this.storeVersion();
        // Don't auto-refresh on startup, just update the stored version
      }
    }
  }

  private storeVersion() {
    const versionInfo: VersionInfo = {
      version: this.currentVersion,
      timestamp: Date.now()
    };
    localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(versionInfo));
  }

  public startVersionCheck(onUpdateAvailable?: () => void) {
    this.onUpdateAvailable = onUpdateAvailable;
    
    // Check immediately
    this.checkVersion();
    
    // Then check periodically
    this.checkInterval = setInterval(() => {
      this.checkVersion();
    }, VERSION_CHECK_INTERVAL);
  }

  public stopVersionCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private async checkVersion() {
    try {
      // Fetch the current index.html with cache-busting
      const response = await fetch(`/index.html?v=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (response.ok) {
        const html = await response.text();

        // Extract version from meta tag
        const versionMatch = html.match(/data-version="([^"]+)"/);
        const remoteVersion = versionMatch ? versionMatch[1] : null;

        console.log('🔍 Version check:', {
          current: this.currentVersion,
          remote: remoteVersion,
          different: remoteVersion !== this.currentVersion
        });

        if (remoteVersion && remoteVersion !== this.currentVersion) {
          console.log('🚀 New version available:', {
            current: this.currentVersion,
            available: remoteVersion
          });

          if (this.onUpdateAvailable) {
            // Custom callback provided - use it
            this.onUpdateAvailable();
          } else {
            // No callback - auto-refresh
            console.log('🔄 Auto-refreshing to new version...');
            this.forceRefresh();
          }
        } else {
          console.log('✅ Version is up to date');
        }
      }
    } catch (error) {
      console.warn('Version check failed:', error);
    }
  }

  public forceRefresh() {
    console.log('🔄 Force refreshing application...');

    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Clear localStorage version to force fresh start
    localStorage.removeItem(VERSION_STORAGE_KEY);

    // Hard reload
    window.location.reload();
  }

  public resetNotification() {
    // Update stored version to current version to dismiss notification
    this.currentVersion = getCurrentVersion();
    this.storeVersion();
    console.log('🔕 Notification reset, current version:', this.currentVersion);
  }
}

export const versionChecker = new VersionChecker();
